import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Stack,
    Typography,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Paper,
    Avatar,
    Divider,
    Chip,
    CircularProgress,
    alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from '../../Configs/axiosInstance';
import { MAIN_URL } from "../../Configs/Urls";

import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';

// Same visual language as the full PatientAddFormOpen page (icon-labelled
// card) so the two "Add Patient" flows feel like one product, just with
// fewer fields. This page renders inside the shared sidebar Layout (see
// Route.js), unlike the standalone /add-patient page.
function Section({ icon, title, subtitle, children }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: { xs: 2.5, sm: 3.5 },
                borderRadius: 3,
                borderColor: 'divider',
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                <Avatar
                    sx={(theme) => ({
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: 'primary.main',
                        width: 40,
                        height: 40,
                    })}
                >
                    {icon}
                </Avatar>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Stack>
            <Divider sx={{ my: 2.5 }} />
            <Stack spacing={2.5}>{children}</Stack>
        </Paper>
    );
}

function ChoiceField({ label, error, options, value, onChange }) {
    return (
        <Box>
            <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mb: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    · choose one
                </Typography>
            </Stack>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                {options.map((option) => {
                    const selected = value === option;
                    return (
                        <Chip
                            key={option}
                            label={option}
                            clickable
                            icon={selected ? <RadioButtonCheckedRoundedIcon /> : <RadioButtonUncheckedRoundedIcon />}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => onChange(option)}
                            sx={{ fontSize: '0.85rem', py: 2.25, px: 0.5 }}
                        />
                    );
                })}
            </Box>
            {error && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}

export default function PatientAddShort() {
    const navigate = useNavigate();

    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const [patientData, setPatientData] = useState({
        name: "",
        age: "",
        contactNumber: "",
        weight: "",
        gender: "",
    });

    const [errors, setErrors] = useState({});

    const handleInputChange = (event) => {
        setErrors({});
        const { name, value } = event.target;
        setPatientData((prevData) => ({ ...prevData, [name]: value }));
    };

    const setField = (field) => (value) => {
        setErrors({});
        setPatientData((prevData) => ({ ...prevData, [field]: value }));
    };

    const validateForm = () => {
        let formErrors = {};
        if (!patientData.name) formErrors.name = "Name is required";
        if (!patientData.age || isNaN(patientData.age) || patientData.age <= 0) formErrors.age = "Valid age is required";
        if (!patientData.contactNumber) formErrors.contactNumber = "Contact number is required";
        if (!patientData.gender) formErrors.gender = "Gender is required";

        setErrors(formErrors);
        return Object.keys(formErrors).length === 0;
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (validateForm()) {
            setLoading(true);

            axios.post(`${MAIN_URL}patients`, patientData)
                .then(() => {
                    setSnackbarMessage("Patient added successfully!");
                    setSnackbarSeverity("success");
                    setOpenSnackbar(true);
                    setOpenDialog(true);
                    setLoading(false);
                })
                .catch((error) => {
                    setOpenDialog(false);
                    setSnackbarMessage(error?.response?.data?.message ? error?.response.data.message : "Error submitting form. Please try again.");
                    setSnackbarSeverity("error");
                    setOpenSnackbar(true);
                    console.error("Error submitting form:", error);
                    setLoading(false);
                });
        } else {
            setSnackbarMessage("Form Not Filled Properly, Please Check and Try Again");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        }
    };

    const handleCloseSnackbar = () => setOpenSnackbar(false);

    return (
        <Box sx={{ width: "100%", maxWidth: 640 }}>
            <Button
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => navigate('/patients')}
                sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
            >
                Back to Patients List
            </Button>

            <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                    <Section
                        icon={<BoltRoundedIcon />}
                        title="Quick Add Patient"
                        subtitle="Just the basics for now — add the rest of their profile later"
                    >
                        <TextField
                            fullWidth
                            name="name"
                            variant="outlined"
                            label="Patient Name"
                            value={patientData.name}
                            onChange={handleInputChange}
                            error={!!errors.name}
                            helperText={errors.name}
                            required
                        />

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                gap: 2,
                            }}
                        >
                            <TextField
                                fullWidth
                                name="age"
                                variant="outlined"
                                label="Age"
                                type="number"
                                value={patientData.age}
                                onChange={handleInputChange}
                                error={!!errors.age}
                                helperText={errors.age}
                                required
                            />
                            <TextField
                                fullWidth
                                name="contactNumber"
                                variant="outlined"
                                label="Contact Number"
                                value={patientData.contactNumber}
                                onChange={handleInputChange}
                                error={!!errors.contactNumber}
                                helperText={errors.contactNumber}
                                required
                            />
                        </Box>

                        <TextField
                            fullWidth
                            name="weight"
                            variant="outlined"
                            label="Weight (kg)"
                            type="number"
                            value={patientData.weight}
                            onChange={handleInputChange}
                            error={!!errors.weight}
                            helperText={errors.weight}
                        />

                        <ChoiceField
                            label="Gender"
                            error={errors.gender}
                            options={["Male", "Female", "Other"]}
                            value={patientData.gender}
                            onChange={setField("gender")}
                        />
                    </Section>

                    <Paper
                        variant="outlined"
                        sx={{
                            p: { xs: 2, sm: 2.5 },
                            borderRadius: 3,
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button
                            variant="text"
                            onClick={() => navigate('/patients')}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <BoltRoundedIcon />}
                            sx={{ borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600 }}
                        >
                            {loading ? "Submitting..." : "Add Patient"}
                        </Button>
                    </Paper>
                </Stack>
            </form>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ fontWeight: 600 }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <Dialog open={openDialog} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
                <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
                    <CheckCircleRoundedIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
                    <DialogTitle sx={{ p: 0, fontWeight: 700 }}>Patient Added</DialogTitle>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        The patient's record has been created. You can fill in the rest of their profile any time from the patient list.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 3 }}>
                    <Button
                        onClick={() => {
                            setOpenDialog(false);
                            setPatientData({ name: "", age: "", contactNumber: "", weight: "", gender: "" });
                        }}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Add Another
                    </Button>
                    <Button
                        onClick={() => navigate('/patients')}
                        variant="contained"
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        Go to Patients List
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
